import type { IconName } from '@/lib/icons';

/** Every place the workspace rail can take its reader. */
export type ContributorWorkspaceDestination =
  | 'overview'
  | 'test'
  | 'requests'
  | 'contracts-pending'
  | 'contracts-signed'
  | 'collection-dashboard'
  | 'collection-profiles'
  | 'collection-contributions'
  | 'collection-initial-audio'
  | 'collection-assistant'
  | 'collection-guidelines';

/** The sections of the collection space, in the order the rail lists them. */
export const COLLECTION_SECTIONS = [
  'dashboard',
  'profiles',
  'contributions',
  'initial-audio',
  'chatbot',
  'guidelines',
] as const;

export type CollectionSection = (typeof COLLECTION_SECTIONS)[number];

const DESTINATION_OF_SECTION: Readonly<Record<CollectionSection, ContributorWorkspaceDestination>> = {
  dashboard: 'collection-dashboard',
  profiles: 'collection-profiles',
  contributions: 'collection-contributions',
  'initial-audio': 'collection-initial-audio',
  chatbot: 'collection-assistant',
  guidelines: 'collection-guidelines',
};

export const COLLECTION_ICONS: Readonly<Record<CollectionSection, IconName>> = {
  dashboard: 'layoutDashboard',
  profiles: 'users',
  contributions: 'fileText',
  'initial-audio': 'mic',
  chatbot: 'messageSquare',
  guidelines: 'shield',
};

/**
 * The section a URL points to. `section` is the parameter the whole workspace
 * uses; `tab` is still read so the links the collector space handed out before
 * the two spaces merged keep working.
 */
export function collectionSectionOf(params: URLSearchParams): CollectionSection {
  const requested = params.get('section') ?? params.get('tab');
  return COLLECTION_SECTIONS.find((section) => section === requested) ?? 'dashboard';
}

export function isCollectionPath(pathname: string): boolean {
  return pathname.endsWith('/collector');
}

/** Which rail entry is lit for the current location. */
export function activeDestination(
  pathname: string,
  params: URLSearchParams,
): ContributorWorkspaceDestination {
  if (isCollectionPath(pathname)) return DESTINATION_OF_SECTION[collectionSectionOf(params)];
  if (pathname.endsWith('/user/contracts')) {
    return params.get('tab') === 'signed' ? 'contracts-signed' : 'contracts-pending';
  }
  if (params.get('section') === 'test') return 'test';
  if (params.get('section') === 'requests') return 'requests';
  return 'overview';
}

/** The link a collection section is reached by. */
export const collectionHref = (base: string, section: CollectionSection) =>
  `${base}/collector?section=${section}`;
